import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PremiumGate } from '@/components/PremiumGate'
import { Spinner } from '@/components/Spinner'
import { EnergyExport } from '@/features/export/EnergyExport'
import * as v5 from '@/api/v5'
import type { V5AddressSummary } from '@/types/api'

function addressLabel(a: V5AddressSummary): string {
  const line = [a.street, a.houseNumber, a.houseNumberAdd].filter(Boolean).join(' ')
  const city = [a.postalCode, a.city].filter(Boolean).join(' ')
  return [line, city].filter(Boolean).join(', ') || a.addressUuid
}

export function DataExportPage() {
  const [addresses, setAddresses] = useState<V5AddressSummary[] | null>(null)
  const [selectedUuid, setSelectedUuid] = useState<string>('')
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    v5.getAddresses()
      .then((list) => {
        if (cancelled) return
        setAddresses(list)
        if (list.length === 1) setSelectedUuid(list[0].addressUuid)
      })
      .catch(() => {
        if (!cancelled) setError('We konden je adressen niet ophalen. Probeer het later opnieuw.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const selected = useMemo(
    () => addresses?.find((a) => a.addressUuid === selectedUuid) ?? null,
    [addresses, selectedUuid]
  )

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-6 pt-12">
      <nav className="mb-5 text-15 text-dark-blue/60">
        <Link to="/services" className="transition-all duration-300 hover:text-dark-blue">
          Diensten
        </Link>{' '}
        <span className="mx-1">/</span>
        <span className="text-dark-blue">Energiedata-export</span>
      </nav>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <h1 className="max-w-md text-28 font-semibold leading-111 lg:text-44">
          Energiedata-export
        </h1>
        <p className="max-w-96 text-base leading-6 text-dark-blue">
          Kies een periode en download de energiedata van je huishouden — afname, teruglevering
          en opwek — als CSV voor je spreadsheet of als overzichtelijke PDF.
        </p>
      </div>

      <div className="mt-8">
        <PremiumGate tier="standaard">
        {error && (
          <div className="card-beige p-5 text-base font-semibold text-red md:p-[30px]">
            {error}
          </div>
        )}

        {!error && addresses === null && (
          <div className="flex items-center gap-3 py-10 text-base text-dark-blue/60">
            <Spinner /> Adressen laden…
          </div>
        )}

        {addresses !== null && addresses.length === 0 && (
          <div className="card-beige p-5 text-base leading-normal text-dark-blue/60 md:p-[30px]">
            Er zijn nog geen adressen gekoppeld aan je account. Voeg je adres toe via de
            easyEnergy-app en kom daarna terug.
          </div>
        )}

        {addresses !== null && addresses.length > 1 && (
          <label className="mb-5 flex max-w-md flex-col gap-1">
            <span className="field-label">Adres</span>
            <select
              className="input"
              value={selectedUuid}
              onChange={(e) => setSelectedUuid(e.target.value)}
            >
              <option value="" disabled>
                Kies een adres…
              </option>
              {addresses.map((a) => (
                <option key={a.addressUuid} value={a.addressUuid}>
                  {addressLabel(a)}
                </option>
              ))}
            </select>
          </label>
        )}

        {selected && (
          <EnergyExport
            key={selected.addressUuid}
            addressUuid={selected.addressUuid}
            addressLabel={addressLabel(selected)}
          />
        )}
        </PremiumGate>
      </div>
    </div>
  )
}
